/**
 * Collection registry for known PFP collections on Tezos
 */

export interface CollectionInfo {
  /** URL-friendly identifier */
  slug: string;
  /** FA2 contract address */
  address: string;
  /** Display name */
  name: string;
  /** Collection description */
  description: string;
  /** Cover/banner image URL */
  coverImage: string;
  /** Thumbnail/avatar image URL */
  thumbnail: string;
  /** Known total supply (optional) */
  totalSupply?: number;
  /** Network this collection exists on */
  network: 'mainnet' | 'ghostnet' | 'sandbox';
  /** Whether the collection has animated NFTs (GIFs) */
  hasAnimated?: boolean;
  /** External links */
  links?: {
    website?: string;
    twitter?: string;
  };
}

/**
 * Registry of supported PFP collections
 */
// Get test collection address from env or use default
const getTestCollectionAddress = (): string => {
  // In browser context, use import.meta.env
  if (typeof import.meta !== 'undefined' && import.meta.env?.VITE_TEST_FA2_CONTRACT) {
    return import.meta.env.VITE_TEST_FA2_CONTRACT;
  }
  // Default to the deployed test FA2 contract
  return 'KT1PCt2hhicDngXiERBBmjrcYG6ffC3eHtWY';
};

export const COLLECTIONS: Record<string, CollectionInfo> = {
  // Sandbox test collection
  'test-pfp': {
    slug: 'test-pfp',
    address: getTestCollectionAddress(),
    name: 'Test PFP Collection',
    description: 'A test PFP collection for marketplace development. Generated with random traits for testing buy/sell flows.',
    coverImage: '',
    thumbnail: '',
    totalSupply: 1000,
    network: 'sandbox',
  },
  // Ghostnet test collection (fx(params) on OBJKT Ghostnet)
  'fxparams-ghostnet': {
    slug: 'fxparams-ghostnet',
    address: 'KT1TtVAyjh4Ahdm8sLZwFnL7tqoLf59XrK2h',
    name: 'fx(params) Ghostnet',
    description: 'Generative art collection on Ghostnet testnet. Used for testing OBJKT marketplace integration.',
    coverImage: '',
    thumbnail: '',
    network: 'ghostnet',
  },
  // Ghostnet test collection (our FA2 with OBJKT listings)
  'test-nfts-ghostnet': {
    slug: 'test-nfts-ghostnet',
    address: 'KT1SMJxhxmVecurXsbrCCyidsP9QDDJEh32y',
    name: 'Test NFTs',
    description: 'Test NFT collection for marketplace development. 200 NFTs with 100 listed at 1-5 XTZ for testing purchase flows.',
    coverImage: '',
    thumbnail: '',
    totalSupply: 200,
    network: 'ghostnet',
  },
  tezzardz: {
    slug: 'tezzardz',
    address: 'KT1LHHLso8zQWQWg1HUukajdxxbkGfNoHjh6',
    name: 'Tezzardz',
    description: 'Tezzardz is a collection of programmatically, randomly generated snazzy little fukrs on the Tezos blockchain.',
    coverImage: '',
    thumbnail: '/collections/tezzardz.png',
    totalSupply: 4200,
    network: 'mainnet',
    links: {
      website: 'https://www.tezzardz.xyz',
      twitter: 'https://twitter.com/tezzardz',
    },
  },
  skeles: {
    slug: 'skeles',
    address: 'KT1HZVd9Cjc2CMe3sQvXgbxhpJkdena21pih',
    name: 'Randomly Common Skeles',
    description: 'Randomly common skeles are randomly generated skeleton .gifs by john karel :)',
    coverImage: '',
    thumbnail: '/collections/skeles.png',
    totalSupply: 10000,
    network: 'mainnet',
    hasAnimated: true,
    links: {
      website: 'https://commonskeles.xyz',
      twitter: 'https://twitter.com/jjjjjjjjjjohn',
    },
  },
  dickrbuttz: {
    slug: 'dickrbuttz',
    address: 'KT1KUHNbSGgjLUrxhNTuizy88GjnqRfnGkvh',
    name: 'Dickrbuttz',
    description: 'Not affiliated with Tezzards or with CryptoDickbutts. Just a poorly made mashup for tez fam. Dkrrrbtzzz!',
    coverImage: '',
    thumbnail: '/collections/dickrbuttz.jpg',
    network: 'mainnet',
    hasAnimated: true,
  },
};

/**
 * Get all collections as an array
 */
export const getAllCollections = (): CollectionInfo[] => {
  return Object.values(COLLECTIONS);
};

/**
 * Get collections based on view mode (for dev toggle)
 * In production, always returns mainnet collections
 * In dev, respects the localStorage setting
 */
export const getCollectionsForView = (viewMode: 'local' | 'mainnet'): CollectionInfo[] => {
  if (viewMode === 'local') {
    return Object.values(COLLECTIONS).filter(c => c.network === 'sandbox');
  }
  return Object.values(COLLECTIONS).filter(c => c.network === 'mainnet');
};

/**
 * Get collections for a specific network
 */
export const getCollectionsByNetwork = (network: 'mainnet' | 'ghostnet' | 'sandbox'): CollectionInfo[] => {
  return Object.values(COLLECTIONS).filter(c => c.network === network);
};

/**
 * Get the current network from environment
 */
export const getCurrentNetwork = (): 'mainnet' | 'ghostnet' | 'sandbox' => {
  const network = import.meta.env.VITE_NETWORK || 'ghostnet';
  console.log('[getCurrentNetwork] VITE_NETWORK env:', import.meta.env.VITE_NETWORK, 'resolved:', network);
  if (network === 'mainnet' || network === 'ghostnet' || network === 'sandbox') {
    return network;
  }
  return 'ghostnet';
};

/**
 * Get collections for the current network
 */
export const getCurrentNetworkCollections = (): CollectionInfo[] => {
  return getCollectionsByNetwork(getCurrentNetwork());
};

/**
 * Look up collection by slug
 */
export const getCollectionBySlug = (slug: string): CollectionInfo | undefined => {
  return COLLECTIONS[slug];
};

/**
 * Look up collection by contract address
 */
export const getCollectionByAddress = (address: string): CollectionInfo | undefined => {
  return Object.values(COLLECTIONS).find(c => c.address === address);
};

/**
 * Check if an address belongs to a known collection
 */
export const isKnownCollection = (address: string): boolean => {
  return Object.values(COLLECTIONS).some(c => c.address === address);
};

/**
 * Get slug from contract address
 */
export const getSlugByAddress = (address: string): string | undefined => {
  const collection = getCollectionByAddress(address);
  return collection?.slug;
};
